/**
 * AP-29-A — Die Modul-Registry.
 *
 * Der **einzige** Ort, an dem steht, welche Module es gibt. Bis zum 10.9.2026 lag
 * diese Liste vierfach im Projekt — im Prisma-Enum `TenantModule`, im
 * `TenantsController`, in der `ModulesPage` und in den i18n-Dateien —, und die
 * Fassung im Controller kannte `PAYROLL` nicht: Die Seite `/settings/modules`
 * hätte das Modul nie anzeigen können, egal was in der Datenbank steht.
 *
 * **Das Enum `TenantModule` ist eingefroren.** Der Modulbestand ist bewusst offen
 * (Festlegung 10.9.2026) — es kommen Module dazu, die heute niemand kennt. Als
 * Aufzählung im Schema kostete jedes davon eine Enum-Migration; als Registry-Eintrag
 * kostet es eine Zeile hier. `tenant.activeModules` ist `String[]`, geprüft wird
 * gegen diese Registry.
 *
 * Diese Datei liegt in `packages/shared-types`, weil Backend, ERP und PWA
 * dieselbe Liste brauchen. Sie enthält **keine** Klartexte (die stehen in den
 * i18n-Dateien unter `modules.names.<KEY>`) und **keine** Preise (die stehen im
 * Angebot).
 *
 * Konzept: `docs/Arbeitspakete/AP-29/AP-29_Modulverwaltung-und-Freischaltung.md`
 */

/**
 * Reifegrad eines Moduls. Entscheidet, ob es verkauft werden darf — ein
 * unfertiges Modul soll auch versehentlich nicht buchbar sein.
 */
export type ModuleStatus =
  /** produktiv, verkaufbar */
  | 'GA'
  /** benutzbar, wird noch bewegt */
  | 'BETA'
  /** Code liegt in `main`, Funktion unfertig (Feature-Flag, AP-21) */
  | 'IN_DEVELOPMENT'
  /** nur ein Name und ein Vorhaben */
  | 'PLANNED'
  /** nicht mehr verkauft; bestehende Buchungen laufen aus */
  | 'RETIRED'

export type ModuleKey =
  /**
   * AP-30-A — Der Kern. **Nie buchbar, bei jedem Mandanten und jedem Benutzer
   * immer aktiv.**
   *
   * Ein gutes Drittel der Endpunkte gehört zu keinem Modul: Benutzer, Branding,
   * Mandanteneinstellungen, Plätze, Modulanfragen — und `core.role.manage`
   * selbst. Ihre Rechte brauchten trotzdem ein Zuhause, denn der Schnitt aus
   * AP-30 5.1 („Rechte ∩ Module mit Platz") hätte für sie keinen Partner und
   * würde sie **jedes Mal wegschneiden**: Nach der Umstellung könnte niemand
   * mehr Benutzer anlegen.
   *
   * Die Alternative wäre eine Sonderliste im Code gewesen — also genau die
   * hartkodierte Liste, gegen die AP-29-A angetreten ist.
   */
  | 'CORE'
  | 'TIME_TRACKING'
  | 'CRM'
  /**
   * AP-28 — Die Artikelverwaltung. Ein Kern-Stammdatum wie `CRM`, aber mit
   * eigenem Schlüssel: `INVENTORY` ist geplant und leer, `CORE` hätte sie an
   * jeden Mandanten verschenkt.
   */
  | 'ARTICLES'
  | 'CORRESPONDENCE'
  | 'PROJECT_MGMT'
  | 'INVENTORY'
  | 'COST_ACCOUNTING'
  | 'REPORTING'
  | 'AI_ASSISTANT'
  | 'PAYROLL'
  /**
   * Der REST-Zugang — **modulübergreifend**, kein Teil der Zeiterfassung
   * (Festlegung 11.9.2026). Die API liefert ohnehin nur, was der Mandant
   * gebucht hat: Ohne CRM ist die Kundenliste leer, ohne Projektzeit gibt es
   * keine Positionen. Ein Zugang je Modul würde also nichts schützen, was
   * nicht schon geschützt ist — er würde nur den Verkauf verkomplizieren.
   */
  | 'INTEGRATION_API'
  /**
   * AP-11-E (E5) — **Eigener Speicher des Mandanten** (BYOS): Dateien liegen in
   * einem S3-kompatiblen Speicher des Kunden statt bei Symphenia.
   *
   * Eigenständig wie `INTEGRATION_API`, kein Baustein einer Gruppe: Es gibt kein
   * „Speicher-Modul", zu dem er gehörte — der `FileService` ist Infrastruktur
   * ohne Schlüssel. Verkauft wird er trotzdem einzeln.
   */
  | 'OWN_STORAGE'
  // ── Bausteine der Zeiterfassung (AP-29-N) ──────────────────────────────────
  // Der Punkt ist **Konvention, keine Syntax**: Zugehörigkeit steht in `parent`,
  // der Punkt macht den Schlüssel nur in Protokollen und Fehlermeldungen lesbar.
  // Wer ihn parst, hat eine zweite Wahrheit gebaut.
  | 'TIME_TRACKING.CORE'
  | 'TIME_TRACKING.PROJECT_TIME'
  | 'TIME_TRACKING.CUSTOM_FIELDS'
  | 'TIME_TRACKING.SCAN'
  | 'TIME_TRACKING.GPS'
  // ── Bausteine des Projektmanagements (AP-35, Phase A) ─────────────────────
  | 'PROJECT_MGMT.CORE'
  | 'PROJECT_MGMT.PROCUREMENT'
  | 'PROJECT_MGMT.BILLING'
  | 'PROJECT_MGMT.CONTROLLING'
  | 'PROJECT_MGMT.IMPORT'
  // ── Kalkulation (AP-37) — Gruppe mit drei Bausteinen ──────────────────────
  | 'CALCULATION'
  | 'CALCULATION.CORE'
  | 'CALCULATION.LV'
  | 'CALCULATION.AI'
  // ── Posteingang (AP-39) — Gruppe mit zwei Bausteinen ──────────────────────
  | 'INBOX'
  | 'INBOX.CORE'
  | 'INBOX.AI'
  // ── Eingangsrechnungen (AP-34) — Gruppe mit drei Bausteinen ──────────────
  | 'INCOMING_INVOICES'
  | 'INCOMING_INVOICES.CORE'
  | 'INCOMING_INVOICES.EINVOICE'
  | 'INCOMING_INVOICES.AI_CAPTURE'
  // ── Baustellendokumente (AP-46) — Gruppe mit einer Basis und je Art einem
  // Baustein. Ersetzt die früheren `PLANNED`-Einträge `SITE_REPORT`, `SITE_DIARY`,
  // `DAILY_REPORT` und `MEETING_MINUTES` (AP-46 4.2).
  | 'SITE_DOCS'
  | 'SITE_DOCS.CORE'
  | 'SITE_DOCS.REGIE'
  | 'SITE_DOCS.ASSEMBLY'
  | 'SITE_DOCS.DIARY'
  | 'SITE_DOCS.MEETING'
  | 'SITE_DOCS.AI'

export interface ModuleDefinition {
  key: ModuleKey
  status: ModuleStatus
  /**
   * AP-29-N: Zu welcher Gruppe gehört dieser Eintrag? Gesetzt = **Baustein**,
   * also ein einzeln verkaufbarer Teil eines Moduls. Fehlt = eigenständiges
   * Modul.
   *
   * Ein Modul, das Bausteine hat, wird dadurch selbst **unbuchbar** (siehe
   * `isBookable`): Gebucht werden ausschließlich Bausteine, „das ganze Modul"
   * ist ein Paket im Katalog. Sonst stünde neben „Modul gebucht" noch
   * „Bausteine gebucht" — zwei Wahrheiten über denselben Zugang.
   */
  parent?: ModuleKey
  /**
   * AP-30-A — Gilt dieser Eintrag **immer**, ohne Buchung und ohne Platz?
   *
   * Genau ein Eintrag trägt das: `CORE`. Er ist kein Produkt, sondern das
   * Zuhause der Rechte, die zu keinem Modul gehören. Für die Ableitung in
   * AP-30 5.1 bedeutet es einen Satz mehr — seine Rechte überstehen den
   * Schnitt gegen die Module mit Platz.
   *
   * Wer hier einen zweiten Eintrag markiert, verschenkt ihn an alle.
   */
  alwaysActive?: boolean
  /**
   * Die Pfad-Äste des **Frontends**, die dieser Eintrag schützt. Trägt die
   * Durchsetzung: Ein ganzer Ast liegt in einem Gate, statt dass jede einzelne
   * Route eine Eigenschaft mitschleppt (AP-29-D) — eine neue Unterseite ist
   * damit automatisch mitgeschützt.
   *
   * **Seit AP-29-N eine Liste und optional.** Eine Liste, weil ein Baustein
   * mehrere Äste haben kann (die eigenen Buchungsfelder liegen unter `fields`,
   * `lists` und `configs`). Optional, weil ein Baustein nur dann einen Ast hat,
   * wenn er eigene Seiten hat: Die quer schneidenden — Projektzeit und GPS sind
   * Felder *innerhalb* der Zeitbuchung — werden an ihrer Konfiguration
   * gesperrt, nicht am Pfad.
   *
   * Das **Backend** schützt seine Controller mit `@RequireModule`; seine Pfade
   * stehen bewusst nicht hier, weil sie mit denen der Oberfläche nicht
   * übereinstimmen (`qr-tags` gegen `/time-tracking/qr-tags`).
   */
  areas?: readonly string[]
  /** Module, ohne die dieses nicht sinnvoll ist. Verhindert das Buchen ins Leere. */
  dependsOn: readonly ModuleKey[]
  /**
   * Enthält das Modul **Kern-Stammdaten**, auf die andere Module zeigen
   * (`Client`, `Project`)? Dann gilt: Ein Modul sperrt die **Verwaltung**, nie
   * den **Bezug** — sonst hätte eine Zeitbuchung eine Kundennummer ohne Kunden
   * (AP-29-C, Abschnitt 7).
   *
   * **Das Flag ist eine Verpflichtung, keine Zustandsbeschreibung** (geprüft am
   * 12.9.2026): Einen modulfreien Lookup-Endpunkt gibt es bis heute an keinem
   * der beiden Module, und `@AllowWithoutModule` wird nirgends verwendet. Die
   * Regel ist trotzdem erfüllt, weil der Kundenname heute nur über
   * `Project.client` gelesen wird (Gate: `PROJECT_MGMT`) und eine Zeitbuchung
   * gar keine `clientId` trägt. Wer hier `true` setzt, übernimmt die Pflicht,
   * beim ersten echten modulfremden Bezug den schlanken Lookup nachzuziehen —
   * siehe `docs/Module/CRM/README.md`, Abschnitt „Buchung und Plätze".
   */
  coreLookup: boolean
  /**
   * AP-29-N2 — Bekommt ein **neu registrierter** Mandant diesen Eintrag
   * automatisch gebucht?
   *
   * **Die Vorgabe ist `false`, und das ist der ganze Zweck des Feldes.** Bis
   * zum 12.9.2026 buchte die Registrierung *alles, was buchbar ist* — ein neu
   * aufgenommener Baustein war damit im selben Augenblick bei jedem neuen
   * Mandanten aktiv, ohne Entscheidung und ohne Preis. Die Asymmetrie fiel
   * nicht auf, weil **Bestands**mandanten nichts bekommen: Dort ist jede
   * Buchung ein Vorgang in der Konsole.
   *
   * Wer einen Eintrag hier auf `true` setzt, verschenkt ihn an jeden, der sich
   * ab dann registriert. Das ist eine Vertriebsentscheidung, keine technische —
   * deshalb steht sie ausdrücklich da und nicht im Verhalten einer Schleife.
   *
   * Voraussetzungen (`dependsOn`) werden beim Anlegen **mitgenommen**: Ein
   * Starteintrag ohne seine Basis wäre eine Buchung ins Leere.
   */
  includedForNewTenants?: boolean
  /**
   * AP-30-A: Der Rechtekatalog dieses Moduls — **gefüllt seit dem 12.9.2026**.
   *
   * Ein Modul bringt seine Rechte genauso mit wie seinen Schlüssel. Ohne diesen
   * Ort würde die Rechteliste die nächste vierfach gepflegte Liste.
   *
   * **Ein Recht steht in genau einer dieser Listen** (AP-30 3a.2). Daraus — und
   * *nur* daraus — entsteht die Zuordnung Recht → Modul, die der Schnitt aus
   * AP-30 5.1 braucht. Der Präfix (`time.`, `crm.`, `core.`) ist Lesbarkeit in
   * Protokollen und Fehlermeldungen, **keine** Syntax: `time.entry.read.own`
   * gehört zu `TIME_TRACKING.CORE`, nicht zu einem Modul namens „time".
   * AP-29-N-B hat den anderen Weg schon einmal genommen und ihn zurückgebaut.
   *
   * **Wohin ein Recht gehört**, entscheidet die Sperre, die seine Endpunkte
   * ohnehin tragen: zu dem Eintrag, den ihr Controller im `@RequireModule`
   * nennt — sonst widerspräche der Schnitt dem Modul-Gate. Endpunkte ohne
   * Modul-Gate gehören nach `CORE`. Ein Test hält beides fest.
   */
  permissions: readonly string[]
}

/**
 * Der Bestand. Reihenfolge = Anzeigereihenfolge in `/settings/modules`.
 *
 * Erledigt (11.9.2026): `INTEGRATION_API` ist ein eigenständiges Modul, und die
 * drei `integration`-Controller tragen es. Damit ist der Befund aus AP-29-G
 * geschlossen — der REST-Zugriff war auf der Website bepreist, aber von
 * niemandem gesperrt.
 *
 * **Es gibt keine Tarifstufen** (Auftraggeber, 12.9.2026). `PRO` und `PRO+` auf
 * der Website waren ein Beispielentwurf; verkauft wird, was hier und im Katalog
 * steht — Modul, Baustein oder Paket. Wer eine Stufe braucht, schnürt sie als
 * Paket, ohne dass jemand deployen muss.
 * Geklärt (AP-46, 15.10.2026): Regiebericht, Montageprotokoll, Bautagebuch und
 * Projektbesprechung sind Bausteine **einer** Gruppe `SITE_DOCS` — gemeinsam
 * definiert, einzeln verkauft.
 */
const REGISTRY = [
  {
    /**
     * AP-30-A — **Kein Produkt, sondern das Zuhause der modullosen Rechte.**
     *
     * Steht bewusst an erster Stelle: Wer die Liste liest, soll sehen, dass es
     * einen Eintrag gibt, der nie verkauft wird. `alwaysActive` nimmt ihn aus
     * dem Verkauf (`isBookable`), aus der Mandantensicht (`visibleModules`) und
     * aus den Startbuchungen — und hält ihn zugleich im Schnitt aus AP-30 5.1.
     *
     * Er hat **keinen** Pfad-Ast: Die Seiten, die an diesen Rechten hängen
     * (Benutzer, Einstellungen, Branding), gehören zu keinem Modul und sind
     * deshalb auch von keinem `ModuleGate` zu sperren.
     */
    key: 'CORE',
    status: 'GA',
    alwaysActive: true,
    dependsOn: [],
    coreLookup: false,
    permissions: [
      // Benutzerverwaltung. `update` und `update_email` sind getrennt, weil sie
      // heute verschieden weit reichen: Benutzer ändern darf auch ein
      // Vorgesetzter, die E-Mail-Adresse nur die Verwaltung.
      'core.user.read',
      'core.user.invite',
      'core.user.update',
      'core.user.update_email',
      'core.user.delete',
      // Das Recht, Rechte zu vergeben. Die Letzter-Administrator-Regel hängt an
      // diesem einen Schlüssel (AP-30 3a.4) — wer ihn umbenennt, muss sie
      // mitnehmen.
      'core.role.manage',
      'core.stats.read',
      'core.tenant.update',
      'core.tenant.delete',
      'core.branding.manage',
      'core.module.read',
      'core.module.request',
      'core.seat.manage',
      'core.ui_defaults.manage',
      // AP-10 Nachtrag 2: Einstellungen des Mailservices (Downloadlinks). Der
      // Mailservice hat keinen Modulschlüssel — seine Rechte gehören hierher.
      'core.mail.manage',
      // AP-10 Nachtrag 1: das eigene Microsoft-365-Postfach verbinden und darüber
      // versenden. Geprüft beim Verbinden **und** beim Senden.
      'core.mail.connect',
      // AP-41: Verträge des Mandanten (AGB, AVV, KI-Zusatz) ansehen, annehmen,
      // zur Kenntnis nehmen, widersprechen. Rechtserheblich — nur Administratoren.
      'core.contract.accept',
      // AP-11-E4: Speicher des Mandanten ansehen — belegt, Kontingent, Aufteilung.
      // Der `FileService` hat keinen Modulschlüssel; sein Recht gehört hierher.
      'core.storage.read',
    ],
  },
  {
    // Gruppe — seit AP-29-N selbst **nicht** buchbar, weil sie Bausteine hat.
    // Der Ast bleibt hier stehen: Er sperrt weiterhin `/time-tracking/*` für
    // jeden, der von der Zeiterfassung gar nichts hat.
    key: 'TIME_TRACKING',
    status: 'GA',
    areas: ['time-tracking'],
    dependsOn: [],
    coreLookup: false,
    permissions: [],
  },
  {
    // Die Basis. Ohne Kommt/Geht gibt es keine Buchung, an der eine
    // Projektzuordnung oder eine Position hinge — deshalb hängen alle übrigen
    // Bausteine an ihr.
    key: 'TIME_TRACKING.CORE',
    parent: 'TIME_TRACKING',
    status: 'GA',
    dependsOn: [],
    includedForNewTenants: true,
    coreLookup: false,
    permissions: [
      // Zeitbuchungen — der Geltungsbereich ist Teil des Rechts (AP-30 4.1).
      'time.entry.read.own',
      'time.entry.read.all',
      'time.entry.create',
      'time.entry.update',
      'time.entry.delete',
      // Fremdbuchung: fuer jemand anderen erfassen. Traegt in der PWA den
      // Reiter „fuer Mitarbeiter" (Zeit, Diaeten, Fehlzeiten) und im ERP das
      // Feld `userId` an POST /time-entries.
      'time.booking.for_others',
      'time.entry.read_running',
      'time.entry.review',
      'time.entry.export',
      'time.daily_work_time.read',
      'time.daily_work_time.export',
      // Fehlzeiten (AP-19)
      'time.absence.read.own',
      'time.absence.read.all',
      'time.absence.manage',
      'time.absence_type.read',
      'time.absence_type.manage',
      // Tagesaufzeichnung / Diaeten
      'time.daily_record.read.own',
      'time.daily_record.read.all',
      'time.daily_record.manage',
      // Monatsbericht (AP-20/AP-24)
      'time.report.read.own',
      'time.report.read.all',
      'time.report.submit',
      'time.report.overview',
      'time.report.manage',
      'time.report.configure',
      // Konfiguration und Auswahllisten der Zeiterfassung
      'time.config.read',
      'time.config.manage',
      'time.selection.read',
      'time.selection.manage',
      'time.user_settings.read.own',
      'time.user_settings.read.all',
      'time.user_settings.manage',
      'time.user_ref.read',
      'time.user_ref.manage',
      'time.privacy.consent',
      // Feldrecht (AP-30-F): Stundensaetze, Betraege, Summen. Ersetzt die
      // Mandanten- und Benutzereinstellung `showPriceFields` — eine Wahrheit
      // ueber die Preisspalte, nicht zwei.
      'time.price.view',
    ],
  },
  {
    // Quer schneidend: kein eigener Endpunkt, sondern `projectId` und die
    // Ref-Verknüpfungen an der Zeitbuchung. Gesperrt wird an der Konfiguration.
    key: 'TIME_TRACKING.PROJECT_TIME',
    parent: 'TIME_TRACKING',
    status: 'GA',
    // KA-46: die Stammdaten-Masken der Ref-Schicht — ihre Endpunkte hängen an diesem Baustein.
    areas: ['time-tracking/projects', 'time-tracking/positions', 'time-tracking/steps', 'time-tracking/activity-types'],
    dependsOn: ['TIME_TRACKING.CORE'],
    includedForNewTenants: true,
    coreLookup: false,
    permissions: [
      // Die Projektstammdaten der Zeiterfassung (Ref-Schicht, AP-09): Projekte,
      // Positionen, Arbeitsschritte, Taetigkeiten. Der Baustein hat hier eigene
      // Gegenstaende, also eigene Rechte (AP-30 3a-#1).
      //
      // Die **Felder** in der Buchung (`projectId`, die Ref-Verknuepfungen)
      // bekommen bewusst KEIN eigenes Recht — sie haengen an der Konfiguration
      // und am Lesefilter aus AP-29-N3 (AP-30 3a-#2).
      'time.project_ref.read',
      'time.project_ref.manage',
    ],
  },
  {
    key: 'TIME_TRACKING.CUSTOM_FIELDS',
    parent: 'TIME_TRACKING',
    status: 'GA',
    // KA-46: nur, was wirklich eigene Felder sind. Die Sammelseiten „Auswahllisten" (Betriebsstätten,
    // Mitarbeiter, Projekte …) und „Konfigurationen" (Stundensätze, QR-Codes, Scan-Regeln) hingen hier
    // mit — ohne diesen Baustein gab es dann keinen Weg zu Stundensätzen, QR-Codes und Betriebsstätten.
    areas: [
      'time-tracking/fields',
      'time-tracking/selection-data',
      'time-tracking/lists/groups',
      'time-tracking/lists/field-options',
      'time-tracking/ref-schemas',
    ],
    dependsOn: ['TIME_TRACKING.CORE'],
    includedForNewTenants: true,
    coreLookup: false,
    permissions: [
      'time.ref_schema.read',
      'time.ref_schema.manage',
      'time.picklist.read',
    ],
  },
  {
    // Auftrags-Scan, Scan-Regeln, QR-Marken und das standortgebundene Stempeln.
    key: 'TIME_TRACKING.SCAN',
    parent: 'TIME_TRACKING',
    status: 'GA',
    areas: ['time-tracking/qr-tags', 'time-tracking/scan-rules'],
    dependsOn: ['TIME_TRACKING.CORE'],
    includedForNewTenants: true,
    coreLookup: false,
    permissions: [
      'time.qr_tag.read',
      'time.qr_tag.manage',
      'time.qr_tag.validate',
      // KA-35: Stempeln im ERP ohne QR-Code — nur im ERP, in der App gilt die Pflicht weiter.
      'time.qr_tag.exempt',
      'time.scan.resolve',
      'time.scan_rule.read',
      'time.scan_rule.manage',
    ],
  },
  {
    // Quer schneidend wie PROJECT_TIME: Koordinaten an der Buchung.
    key: 'TIME_TRACKING.GPS',
    parent: 'TIME_TRACKING',
    status: 'GA',
    dependsOn: ['TIME_TRACKING.CORE'],
    includedForNewTenants: true,
    coreLookup: false,
    permissions: [
      // Quer schneidend: Koordinaten an der Buchung, keine eigenen Gegenstaende.
      // Gesperrt an der Konfiguration, nicht an einem zweiten Schloss (3a-#2).
    ],
  },
  {
    /**
     * AP-29-N: Der REST-Zugang, seit 11.9.2026 ein **eigenständiges** Modul.
     *
     * Er hing kurzzeitig als `TIME_TRACKING.API` an der Zeiterfassung. Das war
     * zu eng: Die Schnittstelle liefert schon heute `users`, `projects` und
     * `activity-types`, nicht nur Zeitdaten. Und ein Betrieb, der nur die
     * Adressverwaltung hat, müsste sonst Kommt/Geht mitkaufen, um an seine
     * eigenen Daten zu kommen (`dependsOn`).
     *
     * **Warum ein Zugang genügt:** Der Mandant hat nur Daten der Module, die er
     * gekauft hat — wo nichts ist, gibt es nichts abzuholen. Ein Zugang je
     * Modul schützte also nichts, was nicht schon geschützt ist.
     */
    key: 'INTEGRATION_API',
    status: 'GA',
    areas: ['integration'],
    dependsOn: [],
    coreLookup: false,
    // Wird verkauft wie jedes andere Modul — also **nicht** mitgeliefert
    // (Entscheidung des Auftraggebers, 12.9.2026). Die frühere Begründung
    // „Unterschied zwischen PRO und PRO+" ist damit hinfällig: Diese Stufen
    // waren ein Entwurf auf der Website und wird es nicht geben. Verkauft wird,
    // was im Katalog als Modul, Baustein oder Paket steht.
    includedForNewTenants: false,
    permissions: [
      'integration.api_key.read',
      'integration.api_key.manage',
    ],
  },
  {
    /**
     * AP-11-E (E5) — eigener S3-kompatibler Speicher des Mandanten.
     *
     * **Der Eintrag sperrt die Einrichtung, nicht den Betrieb** (T-10): Ist
     * eigener Speicher verbunden und wird der Eintrag abbestellt, laufen Lesen
     * und Schreiben weiter — nur die Maske ist weg. Andernfalls wären mit der
     * Kündigung alle Dateien des Mandanten unerreichbar (AP-29-N 5.3:
     * „Abschalten verbirgt, es löscht nicht").
     *
     * Kein Pfad-Ast: Der Abschnitt wohnt auf `/settings/storage`, und diese
     * Seite gehört zu `CORE` — den vollen Speicher sieht auch, wer das hier
     * nicht gebucht hat.
     *
     * `BETA`, solange die Probe gegen ein echtes AWS-Konto aussteht (geprüft
     * ist gegen MinIO).
     */
    key: 'OWN_STORAGE',
    status: 'BETA',
    dependsOn: [],
    coreLookup: false,
    // Wird einzeln freigegeben — auch neuen Mandanten nicht mitgeliefert.
    includedForNewTenants: false,
    permissions: [
      // Einrichten, testen, umziehen — und damit die Zugangsdaten zu fremder
      // Infrastruktur hinterlegen. Ein Recht, weil es ein Vorgang ist.
      'storage.own.manage',
    ],
  },
  {
    key: 'CRM',
    status: 'GA',
    areas: ['clients'],
    dependsOn: [],
    coreLookup: true,
    includedForNewTenants: true,
    permissions: [
      // ── Stammdaten ──────────────────────────────────────────────────────
      'crm.client.read',
      'crm.client.manage',
      // AP-31 4.8 — **endgültiges Löschen**, getrennt vom Deaktivieren. Ein
      // eigenes Recht, weil es ein eigener Vorgang ist: Die Zeile ist weg,
      // mitsamt Ansprechpartnern und Adressen.
      'crm.client.delete',
      // ── Feldrechte (AP-31 6.1) ──────────────────────────────────────────
      // Zwei Gruppen, nicht eine kaufmännische: Zahlungsziel und Skonto
      // braucht, wer Angebote schreibt; Debitoren- und Kreditorenkonten
      // ausschließlich, wer mit der Buchhaltung arbeitet. Wer beides in einem
      // Recht hätte, müsste dem Angebotsschreiber die Kontonummern mitgeben.
      'crm.client.payment.view',
      'crm.client.accounting.view',
    ],
  },
  {
    // AP-35 — Gruppe seit Phase A, selbst **nicht** buchbar. Der Ast bleibt an
    // der Gruppe: Er sperrt `/projects/*` für jeden, der keinen Baustein hat.
    //
    // `coreLookup` bleibt ebenfalls hier: Der Kundenname wird über
    // `Project.client` gelesen (AP-31), und Belege ohne gebuchtes
    // Projektmanagement dürfen einen Projektbezug tragen, nur nicht verwalten
    // (AP-35 3.).
    key: 'PROJECT_MGMT',
    status: 'GA',
    // Phase D: das Modul-Dashboard und die Aufgabenliste liegen auf eigenen
    // Pfaden (`/project-management`, `/project-tasks`) — ohne Eintrag hier
    // stünden sie ohne Modul-Gate im Frontend.
    areas: ['projects', 'project-management', 'project-tasks', 'work-cockpit', 'material-certificates'],
    dependsOn: [],
    coreLookup: true,
    permissions: [],
  },
  {
    // Die Basis: Stamm, Positionen, Phasen, Arbeitsschritte, Journal, Freigaben,
    // Leistungserklärung, Cockpits (AP-35 3.). Ersetzt die frühere Buchung
    // `PROJECT_MGMT` — die Migration benennt sie um, verschenkt aber nichts.
    key: 'PROJECT_MGMT.CORE',
    parent: 'PROJECT_MGMT',
    status: 'GA',
    dependsOn: [],
    includedForNewTenants: true,
    coreLookup: false,
    // Katalog nach Detailblatt 13, 4.1 — endgültig. Rechte, deren Endpunkte
    // erst in späteren Phasen entstehen, stehen trotzdem schon hier: Ein
    // Mandant soll seine Rollen einmal bauen, nicht je Phase nachziehen.
    permissions: [
      // Geltungsbereich im Recht (AP-30): `own` = Projektleitung, Verkauf,
      // Kalkulation, Zuständiger einer Phase, eingeteilt (Blatt 13 4.1).
      'projects.project.read.own',
      'projects.project.read.all',
      // Anlegen, ändern, kopieren, stornieren — kein physisches Löschen.
      'projects.project.manage',
      // Aus `project.manage` herausgelöst (Blatt 13 10.2-1): Positionen pflegen,
      // ohne Kunde und Auftragswert zu ändern.
      'projects.position.manage',
      'projects.workstep.manage',
      'projects.approve.execution',
      'projects.assign.manage',
      'projects.journal.manage.own',
      'projects.journal.manage.all',
      'projects.declaration.issue',
      'projects.certificate.manage',
      'projects.settings.manage',
      // Exporte tragen Namen und Preise — lesen heißt nicht exportieren
      // (Blatt 13 10.2-2).
      'projects.export.run',
      // Feldrecht: Einkaufs- und Verkaufspreise, Sätze, Euro-Spalten.
      'projects.price.view',
    ],
  },
  {
    // Stückliste, CAD-Import, Preisanfrage und Bestellung (AP-35 Phase G).
    // Phase L: `BETA` — gebaut und getestet, Abnahme offen. Buchbar, aber für
    // niemanden automatisch: Die Freigabe geschieht je Mandant in der
    // Betreiber-Konsole (`modul-sichtbarkeit.md`).
    key: 'PROJECT_MGMT.PROCUREMENT',
    parent: 'PROJECT_MGMT',
    status: 'BETA',
    dependsOn: ['PROJECT_MGMT.CORE'],
    coreLookup: false,
    // Phase G: das Einkaufs-Cockpit hat einen eigenen Pfad (Blatt 5 5.3).
    areas: ['purchasing'],
    permissions: [
      'projects.bom.manage',
      'projects.approve.procurement',
    ],
  },
  {
    // Verkaufszeilen, Abrechnungsliste, Arbeitsplatz, Regievorschlag (Phase H).
    // Das Belegwesen steht bewusst **nicht** in `dependsOn`: Ein Baustein, der
    // an einem fremden Modul hängt, wäre ein Paket (AP-29-N). Die Endpunkte,
    // die Belege erzeugen, verlangen `CORRESPONDENCE` an ihrem eigenen
    // `@RequireModule`.
    key: 'PROJECT_MGMT.BILLING',
    parent: 'PROJECT_MGMT',
    status: 'BETA',
    areas: ['project-billing'],
    dependsOn: ['PROJECT_MGMT.CORE'],
    coreLookup: false,
    permissions: [
      'projects.sales.manage',
      'projects.billing.run',
    ],
  },
  {
    // Ist-Daten und Nachkalkulation (Phase I).
    key: 'PROJECT_MGMT.CONTROLLING',
    parent: 'PROJECT_MGMT',
    status: 'BETA',
    dependsOn: ['PROJECT_MGMT.CORE'],
    coreLookup: false,
    permissions: [
      'projects.controlling.read',
    ],
  },
  {
    // LV-Import ONLV, ÖNORM B 2063, GAEB (Phase J) — bleibt `IN_DEVELOPMENT`,
    // bis echte Beispieldateien da sind und die Phase gebaut ist.
    key: 'PROJECT_MGMT.IMPORT',
    parent: 'PROJECT_MGMT',
    status: 'IN_DEVELOPMENT',
    dependsOn: ['PROJECT_MGMT.CORE'],
    coreLookup: false,
    permissions: [
      'projects.import.run',
    ],
  },
  {
    // AP-28 — Kern-Stammdatum wie CRM: Ein Modul sperrt die Verwaltung, nie den
    // Bezug. Wer einen Beleg schreibt, braucht den Artikelstamm — deshalb hängen
    // CORRESPONDENCE und INVENTORY über dependsOn daran und ziehen
    // ihn beim Buchen mit (withDependencies).
    //
    // `coreLookup: true` ist eine Verpflichtung: Sobald ein fremdes Modul die
    // Artikelbezeichnung ohne gebuchtes ARTICLES braucht, kommt der schlanke
    // Lookup mit `@AllowWithoutModule`. Heute liest noch kein fremdes Modul.
    key: 'ARTICLES',
    status: 'GA',
    areas: ['articles'],
    dependsOn: [],
    coreLookup: true,
    includedForNewTenants: false,
    permissions: [
      'articles.article.read',
      'articles.article.manage',
      // Endgültiges Löschen, getrennt vom Deaktivieren (AP-28 4.8).
      'articles.article.delete',
      // Getrennt von `manage`: Ein Import ändert 5.000 Datensätze auf einmal —
      // eine andere Vertrauensfrage als eine Maske.
      'articles.article.import',
      'articles.article.export',
      // Artikelgruppen und Einheiten pflegen. Lesen deckt `article.read`.
      'articles.list.manage',
      // Nummernaufbau und Bildgröße (AP-28 Nachtrag N-17) — wie
      // `correspondence.settings.manage`: ändert, was ein Fremdsystem wiedererkennt.
      'articles.settings.manage',
      // Feldrecht (AP-28 6.3): Lieferanten und Einkaufspreise.
      'articles.purchase.view',
    ],
  },
  {
    key: 'AI_ASSISTANT',
    status: 'BETA',
    // AP-18: `/assistant` ist der Menüpunkt des Assistenten, `/ai` trägt die Einstellungen.
    areas: ['ai', 'assistant'],
    dependsOn: [],
    coreLookup: false,
    // Ein eigenes Verkaufsprodukt: buchbar, aber **standardmäßig nicht aktiv**
    // (Entscheidung des Auftraggebers, 12.9.2026). Damit ist der Befund aus
    // AP-29-G erledigt — das Modul war seit A.1 bei jedem Mandanten gebucht,
    // ohne je in einem Angebot zu stehen.
    includedForNewTenants: false,
    permissions: [
      'ai.config.read',
      'ai.config.manage',
      // AP-18 4.: den Assistenten benutzen — fragen, Werkzeuge lesen lassen, Vorschläge
      // bekommen. Was er dabei sieht und vorschlägt, hängt an den Rechten der Werkzeuge,
      // nicht an diesem.
      'ai.assistant.use',
    ],
  },
  {
    // AP-32 — Belegwesen: Angebot bis Preisanfrage, individuelles Dokument.
    // **Ein** Modul, keine Bausteine (Entscheidung des Auftraggebers): Die
    // Abstufung „nur Lieferscheine, ohne Preise" ist eine Rolle, kein Produkt.
    key: 'CORRESPONDENCE',
    status: 'BETA',
    areas: ['correspondence'],
    // Ein Beleg ohne Empfänger ist keiner (CRM); keine Belegposition ohne
    // Artikelstamm (AP-28). Beide werden beim Buchen mitgenommen.
    dependsOn: ['CRM', 'ARTICLES'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [
      // Die Tür zum Modul: Liste, Zwischenablage, Auswahllisten der Maske. Sie
      // gibt für sich **keinen** Beleg frei — welche Arten jemand sieht, sagen
      // die Rechte je Art darunter. Ohne dieses eine Recht bräuchte die Liste
      // ein ODER über neun Leserechte, und das kennt der Guard bewusst nicht.
      'correspondence.document.read',
      // Je Belegart lesen/anlegen/ändern/löschen (AP-32 10.2). Anzahlungs-,
      // Teil-, Schlussrechnung und Storno hängen an `invoice`.
      'correspondence.offer.read',
      'correspondence.offer.create',
      'correspondence.offer.update',
      'correspondence.offer.delete',
      'correspondence.order_confirmation.read',
      'correspondence.order_confirmation.create',
      'correspondence.order_confirmation.update',
      'correspondence.order_confirmation.delete',
      'correspondence.delivery_note.read',
      'correspondence.delivery_note.create',
      'correspondence.delivery_note.update',
      'correspondence.delivery_note.delete',
      'correspondence.invoice.read',
      'correspondence.invoice.create',
      'correspondence.invoice.update',
      'correspondence.invoice.delete',
      'correspondence.credit_note.read',
      'correspondence.credit_note.create',
      'correspondence.credit_note.update',
      'correspondence.credit_note.delete',
      'correspondence.dunning.read',
      'correspondence.dunning.create',
      'correspondence.dunning.update',
      'correspondence.dunning.delete',
      'correspondence.purchase_order.read',
      'correspondence.purchase_order.create',
      'correspondence.purchase_order.update',
      'correspondence.purchase_order.delete',
      'correspondence.inquiry.read',
      'correspondence.inquiry.create',
      'correspondence.inquiry.update',
      'correspondence.inquiry.delete',
      'correspondence.custom_document.read',
      'correspondence.custom_document.create',
      'correspondence.custom_document.update',
      'correspondence.custom_document.delete',
      // Belegartübergreifend.
      // Festschreiben — der Punkt ohne Rückweg.
      'correspondence.document.post',
      'correspondence.document.cancel',
      'correspondence.document.send',
      'correspondence.payment.manage',
      'correspondence.settings.manage',
      // Feldrecht: Einzelpreise, Rabatte, Zeilenbeträge, Summen, Zahlungen.
      'correspondence.price.view',
    ],
  },
  {
    // AP-37 — **Kalkulation.** Ein eigener Bereich, aufgebaut wie ein Projekt,
    // aber nicht Teil des Projektmanagements (Auftraggeber 14.9. und 24.9.2026).
    // Die Gruppe ist selbst nicht buchbar; der Ast `/calculations` hängt an ihr
    // und sperrt die Seiten für jeden, der keinen Baustein hat.
    //
    // `coreLookup: false`: Auf eine Kalkulation zeigt nur `Project.calculationId`,
    // und das liest das Projektmodul über einen schlanken Lookup (AP-37 3., 8.).
    //
    // `BETA` seit 25.9.2026 (Auftraggeber): `.CORE` und `.LV` sind in der
    // Betreiber-Konsole freischaltbar, `.AI` seit 30.9.2026 ebenfalls.
    // Bestandsmandanten bekommen nichts automatisch.
    key: 'CALCULATION',
    status: 'BETA',
    areas: ['calculations'],
    dependsOn: [],
    coreLookup: false,
    permissions: [],
  },
  {
    // AP-37 3. — Kalkulationen, Blatt, Sätze, Aufschläge, Angebot, Ausdruck.
    // „Angebot erstellen" verlangt `CORRESPONDENCE`, „Projekt anlegen"
    // `PROJECT_MGMT.CORE` — beides an ihrem eigenen `@RequireModule`, nicht hier
    // (ein Baustein hängt nur innerhalb seiner Gruppe ab, AP-29-N).
    key: 'CALCULATION.CORE',
    parent: 'CALCULATION',
    status: 'BETA',
    dependsOn: [],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [
      // AP-37 7. — Geltungsbereich: eigene = Kalkulant oder angelegt.
      'calculation.calculation.read.own',
      'calculation.calculation.read.all',
      // Anlegen, ändern, Positionen, Blatt, Sätze und Schema der Kalkulation,
      // Status, neue Fassung, kopieren, Vorlagen — im Geltungsbereich von `read`.
      'calculation.calculation.manage',
      // Festschreiben, Angebot erstellen, Projekt anlegen — der Punkt ohne Rückweg.
      'calculation.calculation.submit',
      'calculation.settings.manage',
      // Feldrecht: Einzelkosten, Sätze, Aufschläge, Deckungsbeitrag. Ohne es ist
      // das Blatt nur lesbar (Rückweg-Falle, AP-37 7.).
      'calculation.cost.view',
      // Detailblatt O 12.: Cockpit „Materialanfragen" — anfragen, „nicht anfragen",
      // Antworten erfassen (dazu `cost.view`). Welche Zeilen jemand sieht, bestimmt
      // weiter `read.own`/`.all`; übernehmen verlangt `manage` + `cost.view` (O-3).
      'calculation.request.manage',
    ],
  },
  {
    // AP-37 3. — LV einlesen, auspreisen, ausgeben. Eigenes Verkaufsargument
    // (öffentliche Ausschreibungen); ein Schlosser mit Privatkunden braucht es nicht.
    key: 'CALCULATION.LV',
    parent: 'CALCULATION',
    status: 'BETA',
    dependsOn: ['CALCULATION.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: ['calculation.lv.import', 'calculation.lv.export'],
  },
  {
    // AP-37 4.18 — Vorschläge aus Unterlagen per KI (Phase N). **Keine eigenen
    // Rechte** (Muster AP-34 `.AI_CAPTURE`): Vorschlagen lassen ist Bearbeiten
    // des Blatts. Quer schneidend — gesperrt werden Knopf und Aktion, kein Ast.
    // `BETA` seit 30.9.2026 (Auftraggeber): freischaltbar in der Betreiber-Konsole; ohne
    // eigenen Schlüssel zählt das Symphenia-Kontingent des Mandanten (`TenantAiConfig.monthlyLimit`).
    key: 'CALCULATION.AI',
    parent: 'CALCULATION',
    status: 'BETA',
    dependsOn: ['CALCULATION.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [],
  },
  {
    // AP-39 — Posteingang: ein Ort für alles, was von außen kommt (Angebote,
    // Auftragsbestätigungen, Lieferscheine, Rechnungen). Gruppe mit zwei Bausteinen.
    key: 'INBOX',
    // BETA seit 28.9.2026 (PE-21): A–C gebaut — in der Betreiber-Konsole freischaltbar, bei niemandem von selbst.
    status: 'BETA',
    areas: ['inbox'],
    dependsOn: [],
    coreLookup: false,
    permissions: [],
  },
  {
    // AP-39 4. — ablegen (Ziehen, Ordner, Eingangsadresse), erkennen nach Regeln,
    // zuordnen, Eingänge am Beleg, Zähler im Menükopf. Bei Bestandsmandanten aus.
    key: 'INBOX.CORE',
    parent: 'INBOX',
    status: 'BETA',
    dependsOn: [],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [
      // Posteingang sehen, Dokumente öffnen — samt Preisen darin (PE-15).
      'inbox.item.read',
      // ablegen, zuordnen, ignorieren, Zuständige setzen, löschen.
      'inbox.item.manage',
      // Eingangsadresse, Ordner, Regeln, KI.
      'inbox.settings.manage',
    ],
  },
  {
    // AP-39 7.5 — Art und Werte per KI. Keine eigenen Rechte (Muster CALCULATION.AI):
    // Auslesen lassen ist Bearbeiten des Eintrags. Eigener Baustein wegen der Kosten (PE-16).
    // `BETA` seit 5.10.2026 (Auftraggeber): freischaltbar in der Betreiber-Konsole. Solange er nicht
    // buchbar war, las im Posteingang nie eine KI — ein Angebot an der Preisanfrage blieb ohne Preise.
    key: 'INBOX.AI',
    parent: 'INBOX',
    status: 'BETA',
    dependsOn: ['INBOX.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [],
  },
  {
    // AP-34 — Eingangsrechnungen: jede Lieferantenrechnung einmal erfassen, dann
    // prüfen, abgleichen, freigeben, zahlen, übergeben. Gruppe mit drei Bausteinen.
    key: 'INCOMING_INVOICES',
    // BETA seit 4.10.2026 (Auftraggeber): in der Betreiber-Konsole freischaltbar, bei niemandem von
    // selbst. Gebaut ist die Erfassung (Phasen A–C); Abgleich, Freigabe, Zahlung, Übergabe folgen.
    status: 'BETA',
    areas: ['incoming-invoices'],
    dependsOn: [],
    coreLookup: false,
    permissions: [],
  },
  {
    // AP-34 4. — Erfassung, Abgleich, Freigabelauf, offene Posten, Zahlungslauf,
    // Subunternehmer, Übergabe, Projektkosten. Die Basis der Gruppe. Braucht die
    // Geschäftspartner (Lieferanten), **nicht** Artikel und nicht das Belegwesen:
    // Eine Rechnung ohne Artikel und ohne Bestellung ist der Normalfall für Miete,
    // Telefon und Subunternehmer.
    //
    // `CRM` steht trotzdem bewusst **nicht** in `dependsOn`: Ein Baustein, der an
    // einem fremden Modul hängt, wäre ein Paket (AP-29-N) — und die Gruppe hätte
    // keine Basis mehr. Dasselbe Muster wie `PROJECT_MGMT.BILLING`.
    key: 'INCOMING_INVOICES.CORE',
    parent: 'INCOMING_INVOICES',
    status: 'BETA',
    dependsOn: [],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [
      // Lesen mit Geltungsbereich (AP-34 19.): eigene = selbst erfasst oder mit
      // Freigabeaufgabe; alle anderen Verben setzen das Lesen des Datensatzes voraus.
      'incoming_invoices.invoice.read.own',
      'incoming_invoices.invoice.read.all',
      // anlegen, hochladen, einlesen
      'incoming_invoices.invoice.create',
      // Entwurf bearbeiten, „zur Bearbeitung öffnen"
      'incoming_invoices.invoice.update',
      'incoming_invoices.invoice.delete',
      // abschließen (11.6)
      'incoming_invoices.invoice.release',
      // stornieren, zurückweisen, Stornobuchung
      'incoming_invoices.invoice.cancel',
      // eigene Aufgaben freigeben oder ablehnen
      'incoming_invoices.approval.decide',
      // Lauf abbrechen, neu zuweisen, alle Aufgaben sehen
      'incoming_invoices.approval.manage',
      // Zahlungen, Rücklass freigeben, HFU-Prüfung
      'incoming_invoices.payment.manage',
      // Zahlungssperre aufheben (12.5)
      'incoming_invoices.payment.unblock',
      // Zahlungslauf: anlegen und freigeben sind zwei Rechte (Vier-Augen, 12.3)
      'incoming_invoices.payment_run.create',
      'incoming_invoices.payment_run.release',
      'incoming_invoices.export.create',
      'incoming_invoices.export.revoke',
      // Reiter im Projekt (14.3)
      'incoming_invoices.project_costs.read',
      // Einstellungen, Regeln, Steuercodes, Exportprofile, Gemeinkosten-Kategorien
      'incoming_invoices.settings.manage',
    ],
  },
  {
    // AP-34 16. — E-Rechnung einlesen. Keine eigenen Rechte: Einlesen ist Anlegen.
    // Quer schneidend — gesperrt wird der Upload-Weg, nicht ein Pfad-Ast.
    key: 'INCOMING_INVOICES.EINVOICE',
    parent: 'INCOMING_INVOICES',
    status: 'IN_DEVELOPMENT',
    dependsOn: ['INCOMING_INVOICES.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [],
  },
  {
    // AP-34 17. — PDF und Scan per KI lesen. Keine eigenen Rechte (Muster
    // CALCULATION.AI, INBOX.AI); eigener Baustein wegen Kosten und Einwilligung.
    // `BETA` seit 4.10.2026 (Auftraggeber): in der Betreiber-Konsole buchbar, damit die Probe mit
    // echten Rechnungen laufen kann — die Spezifikation sah `BETA` erst nach dieser Probe vor (17.6).
    // Die Probe mit 30 Rechnungen hat der Auftraggeber am 10.10.2026 als bestanden gemeldet: Der
    // Baustein ist damit freigegeben und bleibt `BETA` wie die Gruppe und `.CORE`.
    key: 'INCOMING_INVOICES.AI_CAPTURE',
    parent: 'INCOMING_INVOICES',
    status: 'BETA',
    dependsOn: ['INCOMING_INVOICES.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [],
  },
  {
    // Gerüst aus AP-21-G, Fachpaket AP-22. Seit dem 3.10.2026 `BETA` und damit
    // in der Betreiber-Konsole buchbar (Teil 2 ist gebaut, Abnahme offen). Kein
    // Starteintrag: Wer es bekommt, bekommt es einzeln über die Konsole.
    key: 'PAYROLL',
    status: 'BETA',
    areas: ['payroll'],
    dependsOn: [],
    coreLookup: false,
    permissions: [
      'payroll.status.read',
      // AP-22-B: Stammdaten und versionierte Konfiguration. Lesen und Pflegen
      // getrennt — wer die Lohnverrechnung vorbereitet, muss Sollzeiten und
      // Sätze einsehen können, ohne sie ändern zu dürfen.
      'payroll.work_schedule.read',
      'payroll.work_schedule.manage',
      'payroll.holiday.read',
      'payroll.holiday.manage',
      'payroll.setting.read',
      'payroll.setting.manage',
      'payroll.collective_agreement.read',
      'payroll.collective_agreement.manage',
      // AP-22-D: eigene Fehlzeit-Arten und die Tageszeilen der Erfassung.
      'payroll.absence_type.read',
      'payroll.absence_type.manage',
      'payroll.day_entry.read',
      'payroll.day_entry.manage',
      // AP-38-D: die eigene Mitarbeiterliste der Lohnverrechnung — statt
      // `core.user.read`, das einer externen Lohnverrechnung die ganze
      // Benutzerverwaltung zeigte. Und wer sie machen darf: Das vergibt der
      // Betrieb, nicht die Lohnverrechnung selbst (Vorlage „Lohnverrechnung").
      'payroll.employee.read',
      // AP-38-F: Mitarbeiter ohne Zugang anlegen und pflegen — auch die externe
      // Lohnverrechnung, die im Betrieb „nur PAYROLL" Neueintritte selbst erfasst.
      'payroll.employee.manage',
      'payroll.access.manage',
      // AP-22 Teil 2 (G1): der Lohnmonat — Stand lesen und fertigstellen. Erst ein
      // fertiggestellter Lohnmonat lässt sich exportieren.
      'payroll.month.read',
      'payroll.month.finalize',
      // G2: was mit einem fertiggestellten Lohnmonat geschehen darf (gesperrt ·
      // mit Grund · frei). Entscheidet der Administrator der Firma — die Vorlage
      // „Lohnverrechnung" trägt dieses Recht nicht.
      'payroll.closing.configure',
      // H: hinterlegte Abgabezettel ansehen bzw. hinterlegen und entfernen.
      'payroll.slip.read',
      'payroll.slip.manage',
      // I: Abgabezettel von der KI lesen lassen — einschalten samt Datenschutz-Hinweis.
      // Bewusst nicht in der Vorlage „Lohnverrechnung": Der Zettel geht an einen KI-Anbieter.
      'payroll.slip.configure',
      // F: BMD-Datei erzeugen und frühere abrufen / Lohnarten-Zuordnung und Format einstellen.
      'payroll.bmd.export',
      'payroll.bmd.configure',
    ],
  },
  {
    // AP-46 — Baustellendokumente: Regiebericht, Montageprotokoll, Bautagebuch und
    // Projektbesprechung — Schriftstücke, die zu einem Projekt gehören, dort entstehen,
    // wo gearbeitet wird, und ab der Freigabe unveränderlich sind. Gruppe, nicht
    // buchbar; der Status folgt der Basis.
    //
    // Kein `coreLookup`: Auf ein Baustellendokument zeigt kein anderes Modul mit einem
    // Fremdschlüssel; die Nähte (Verbrauch, Regievorschlag, Ablage) laufen über
    // benannte Dienste.
    key: 'SITE_DOCS',
    // `BETA` seit 16.10.2026 (Auftraggeber): mit `.CORE`, `.REGIE` und `.AI` in der Betreiber-Konsole
    // freischaltbar, bei niemandem von selbst aktiv — die Abnahme läuft mit echten Mandanten.
    status: 'BETA',
    dependsOn: [],
    coreLookup: false,
    permissions: [],
  },
  {
    // AP-46 4.1 — die Grundlage **ohne eigene Dokumentart** (BD-2): Akte, Lesen, PDF,
    // Einstellungen je Art, Nummernkreise und Geräteblöcke, Offline-Ablage, Freigabe
    // vor Ort, Kopie per Mail, Journalzeile, Projektbezug aus drei Quellen.
    //
    // Keine Projektverwaltung am Endpunkt (AP-46 24.): Was nur mit ihr geht, prüft der
    // Dienst an der Buchung des Mandanten. Lesen, PDF und Kopie hängen hier, nicht am
    // Baustein der Art — wer eine Art kündigt, verliert das Erfassen, nie das
    // Nachsehen (BD-19).
    key: 'SITE_DOCS.CORE',
    parent: 'SITE_DOCS',
    // `BETA` seit 16.10.2026 (Auftraggeber) — freischaltbar in der Konsole, `includedForNewTenants: false`.
    status: 'BETA',
    areas: ['site-docs', 'settings/site-docs'],
    dependsOn: [],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [
      // Lesen, PDF und Dateien — der Geltungsbereich ist Teil des Rechts.
      'sitedocs.document.read.own',
      'sitedocs.document.read.all',
      // Fremde Entwürfe bearbeiten, abschließen, verwerfen, an ein Gerät geben;
      // Gegenzeichnung an fremden Dokumenten; für einen anderen übertragen. Kein
      // `.all`: Eigene Entwürfe öffnet das Anlegerecht der Art, eine `.own`-Hälfte gäbe
      // es nicht (AP-46 IST-STAND, Abweichungen).
      'sitedocs.document.edit_others',
      // Stornieren mit oder ohne Ersatz; Bestätigung per Link zurückziehen.
      'sitedocs.document.void',
      // Kopie per E-Mail, Bestätigung neu senden, Prüfseite erneuern.
      'sitedocs.document.send',
      'sitedocs.export.run',
      // Einstellungen je Art, Nummernblöcke, Nummernlücken, Quarantäne.
      'sitedocs.settings.manage',
    ],
  },
  {
    // AP-27 — Regiebericht. Ersetzt `SITE_REPORT`; `dependsOn: ['ARTICLES']` entfällt
    // (Gruppenregel: keine Abhängigkeit über die Gruppe hinaus) — die Artikelauswahl
    // prüft `ARTICLES` an ihrem eigenen Endpunkt. `REGIE` bleibt deutsch, weil der Code
    // den Begriff längst führt (`ProjectRegieRate`, `REGIE_ENTRY_IN_SITE_REPORT`).
    key: 'SITE_DOCS.REGIE',
    parent: 'SITE_DOCS',
    // `IN_DEVELOPMENT` seit AP-27-A (15.10.2026); `BETA` seit 16.10.2026 (Auftraggeber) — vor der Abnahme
    // freischaltbar, damit sie mit echten Mandanten laufen kann; bei niemandem von selbst aktiv.
    status: 'BETA',
    areas: ['site-docs/new/regie'],
    dependsOn: ['SITE_DOCS.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [
      // Anlegen, eigene Entwürfe bearbeiten, zur Unterschrift vorlegen, abschließen,
      // Nachtrag; Vorbelegung und Zwischenspeicher fürs Gerät.
      'sitedocs.regie.create',
      // AP-27 10.: Zuordnungen der Zeilen pflegen — Buchungen, Artikel, Einheit —, auch nach dem
      // Abschluss, außer an abgerechneten Zeilen.
      'sitedocs.regie.assign',
      // AP-27 7., 19.: in die Ablage legen; Regiezeilen fürs eigene ERP exportieren.
      'sitedocs.regie.bill',
    ],
  },
  {
    // AP-47 — Montageprotokoll. `ASSEMBLY` folgt `isAssembly` an den Arbeitsschritten
    // (AP-35 Nachtrag 9/10). `IN_DEVELOPMENT` seit AP-47-A, `BETA` seit AP-47-H (16.10.2026) —
    // freischaltbar, bei niemandem von selbst aktiv. Kein `PROJECT_MGMT`: Was nur mit der
    // Projektverwaltung geht, prüft der Dienst an der Buchung (AP-46 BD-48).
    key: 'SITE_DOCS.ASSEMBLY',
    parent: 'SITE_DOCS',
    status: 'BETA',
    areas: ['site-docs/new/assembly', 'site-docs/defects'],
    dependsOn: ['SITE_DOCS.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [
      // Anlegen, eigene Entwürfe bearbeiten, vorlegen, abschließen, Nachtrag, Abnahme anlegen.
      'sitedocs.assembly.create',
      // AP-47 6.3, 4.1: Mängel als behoben vermerken und zurücknehmen; Bauteile Position und
      // Arbeitsschritt zuordnen.
      'sitedocs.assembly.manage',
    ],
  },
  {
    // Bautagebuch, Tagesberichte inbegriffen (BD-3 — `DAILY_REPORT` entfällt: ein
    // Tagesbericht ist ein Tag im Bautagebuch). Ersetzt `SITE_DIARY`.
    key: 'SITE_DOCS.DIARY',
    parent: 'SITE_DOCS',
    // AP-48 (16.10.2026): gebaut, `BETA` — freischaltbar über die Konsole, bei niemandem von selbst
    // aktiv; Abnahme offen (AP-48 19.).
    status: 'BETA',
    // AP-48: Bautagebuch des Projekts (Kalender) und Behinderungsliste.
    areas: ['site-docs/new/diary', 'site-docs/diary'],
    dependsOn: ['SITE_DOCS.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: ['sitedocs.diary.create'],
  },
  {
    // AP-49 — Projektbesprechung. Ersetzt `MEETING_MINUTES`. `IN_DEVELOPMENT` seit AP-49-A, `BETA` seit AP-49-H
    // (16.10.2026) — wie Regiebericht und Diktat vor der Abnahme freischaltbar; bei niemandem von selbst aktiv.
    key: 'SITE_DOCS.MEETING',
    parent: 'SITE_DOCS',
    status: 'BETA',
    // AP-49 8.: die Liste offener Punkte (`/site-docs/meeting/open-items`) hängt am Baustein.
    areas: ['site-docs/new/meeting', 'site-docs/meeting'],
    dependsOn: ['SITE_DOCS.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: ['sitedocs.meeting.create'],
  },
  {
    // AP-46 21. — Diktat mit KI: aus dem diktierten oder getippten Text einen Vorschlag für den Inhalt
    // der Art. Keine eigene Seite: Der Knopf sitzt im Formular der Art, der Schalter samt Einwilligung
    // unter `settings/site-docs`. Diktieren über die Tastatur geht ohne den Baustein.
    // `IN_DEVELOPMENT` seit Phase H (16.10.2026); `BETA` seit 16.10.2026 (Auftraggeber, mit AP-27-G) —
    // freischaltbar; wirksam erst mit dem Schalter samt Einwilligung in den Einstellungen.
    key: 'SITE_DOCS.AI',
    parent: 'SITE_DOCS',
    status: 'BETA',
    dependsOn: ['SITE_DOCS.CORE'],
    coreLookup: false,
    includedForNewTenants: false,
    permissions: [],
  },
  {
    key: 'INVENTORY',
    status: 'PLANNED',
    areas: ['inventory'],
    // AP-28: Bestände führt man an Artikeln.
    dependsOn: ['ARTICLES'],
    coreLookup: false,
    permissions: [],
  },
  {
    key: 'COST_ACCOUNTING',
    status: 'PLANNED',
    areas: ['cost-accounting'],
    dependsOn: [],
    coreLookup: false,
    permissions: [],
  },
  {
    key: 'REPORTING',
    status: 'PLANNED',
    areas: ['reporting'],
    dependsOn: [],
    coreLookup: false,
    permissions: [],
  },
  // `as const satisfies` statt einer Typ-Annotation: Die Annotation loescht die
  // Literale, und ohne sie gibt es keinen Typ `Permission`. `satisfies` prueft
  // trotzdem jeden Eintrag gegen `ModuleDefinition` — ein Tippfehler im Status
  // oder ein fehlendes `dependsOn` bricht weiter den Build.
] as const satisfies readonly ModuleDefinition[]

/**
 * AP-30-A — Jedes Recht, das es gibt, als **Vereinigung der Literale**.
 *
 * Der Typ steht hier und nicht in `permissions.ts`, weil er nur aus dem
 * const-behafteten `REGISTRY` ableitbar ist: Die Typ-Annotation an
 * `MODULE_REGISTRY` löscht die Literale wieder, und genau diese Annotation
 * brauchen die Funktionen weiter unten, um die optionalen Felder (`parent`,
 * `alwaysActive`, `includedForNewTenants`) überhaupt lesen zu können.
 *
 * Deshalb zwei Namen für eine Liste: `REGISTRY` trägt die Literale, das
 * exportierte `MODULE_REGISTRY` trägt den Typ. Ein Tippfehler in
 * `@RequirePermission` bricht damit den Type-Check, statt still eine Sperre zu
 * bauen, die niemand jemals erfüllt.
 */
export type Permission = (typeof REGISTRY)[number]['permissions'][number]

/**
 * Der Bestand, wie ihn alle lesen. Dieselbe Liste wie `REGISTRY` — nur mit dem
 * Typ statt mit den Literalen.
 */
export const MODULE_REGISTRY: readonly ModuleDefinition[] = REGISTRY

/** Alle Modulschlüssel in Anzeigereihenfolge. */
export const MODULE_KEYS: readonly ModuleKey[] = MODULE_REGISTRY.map((m) => m.key)

const REGISTRY_POSITION = new Map<string, number>(MODULE_KEYS.map((key, i) => [key, i]))

/**
 * Bringt Modulschlüssel in die Reihenfolge der Registry.
 *
 * Gebraucht wird das überall, wo eine Modulliste aus der **Datenbank** kommt und
 * in eine Antwort geht. Ohne `ORDER BY` liefert Postgres die Zeilen so, wie der
 * Ausführungsplan sie findet — über den Index `(tenantId, moduleKey)`
 * alphabetisch, beim Durchlauf der Tabelle in der Reihenfolge der Anlage —, und
 * der Plan wechselt mit der Statistik der Tabelle, nicht mit ihrem Inhalt. Am
 * 12.10.2026 bekam so derselbe Mandant bei unveränderten Buchungen von
 * `GET /auth/me` zwei verschiedene Listen.
 *
 * Die Registry und nicht das Alphabet, weil die Modulseite des Mandanten und
 * der Modulstand in der Betreiber-Konsole schon so geordnet sind (`MODULE_KEYS`,
 * `visibleModules`, `componentsOf`) — eine zweite Ordnung daneben wäre die
 * nächste Stelle, an der zwei Antworten dieselbe Lage verschieden zeigen.
 *
 * Schlüssel, die die Registry nicht kennt, bleiben erhalten und stehen am Ende,
 * unter sich nach dem Schlüssel; über sie urteilt der Aufrufer (`isModuleKey`),
 * nicht diese Funktion. Das Ergebnis hängt damit nur davon ab, **welche**
 * Schlüssel hereinkommen, nie davon, in welcher Reihenfolge.
 */
export function inRegistryOrder<T extends string>(keys: readonly T[]): T[] {
  const stelle = (key: string) => REGISTRY_POSITION.get(key) ?? MODULE_KEYS.length
  return [...keys].sort((a, b) => stelle(a) - stelle(b) || (a < b ? -1 : a > b ? 1 : 0))
}

/** Status, in denen ein Modul verkauft werden darf. */
const BOOKABLE_STATUSES: readonly ModuleStatus[] = ['GA', 'BETA']

export function getModule(key: string): ModuleDefinition | undefined {
  return MODULE_REGISTRY.find((m) => m.key === key)
}

export function isModuleKey(key: string): key is ModuleKey {
  return MODULE_REGISTRY.some((m) => m.key === key)
}

/**
 * Hat dieser Eintrag Bausteine? Dann ist er eine **Gruppe** — ein Modul, das nur
 * noch in Teilen verkauft wird (AP-29-N).
 */
export function isModuleGroup(key: string): boolean {
  return MODULE_REGISTRY.some((m) => m.parent === key)
}

/**
 * Die Bausteine einer Gruppe, in Registry-Reihenfolge. Leer für ein Modul ohne
 * Zerlegung — und genau deshalb darf der Aufrufer nicht zwischen „Modul" und
 * „Gruppe" unterscheiden müssen.
 */
export function componentsOf(key: string): ModuleDefinition[] {
  return MODULE_REGISTRY.filter((m) => m.parent === key)
}

/**
 * Zu welcher Gruppe gehört dieser Schlüssel? Ein Baustein liefert seine Gruppe,
 * ein eigenständiges Modul **sich selbst**.
 *
 * Das ist die Bezugsgröße der Plätze: Ein Platz gilt je Gruppe, nicht je
 * Baustein (AP-29-N, Abschnitt 4). Wer die Platzfrage stellt, fragt hierüber.
 */
export function groupOf(key: string): ModuleKey | undefined {
  const def = getModule(key)
  if (!def) return undefined
  return def.parent ?? def.key
}

/**
 * Die Schlüssel samt allem, was sie voraussetzen — transitiv und ohne Doppelte.
 *
 * Beim Buchen wird die Voraussetzung **mitgenommen**, nicht abgelehnt: Wer
 * Projektzeit kauft, meint die Zeiterfassung dazu. Die Reihenfolge auswendig
 * lernen zu müssen, wäre eine Hürde ohne Zweck — und die Ablehnung träfe
 * ausgerechnet den, der das Paket richtig zusammengestellt hat.
 *
 * Unbekannte Schlüssel bleiben unverändert stehen; über sie urteilt der
 * Aufrufer (`isModuleKey`), nicht diese Funktion.
 */
export function withDependencies(keys: readonly string[]): string[] {
  const ergebnis = new Set<string>()

  const nimm = (key: string) => {
    if (ergebnis.has(key)) return
    ergebnis.add(key)
    for (const dep of getModule(key)?.dependsOn ?? []) nimm(dep)
  }

  for (const k of keys) nimm(k)
  return [...ergebnis]
}

/**
 * Wer hängt an diesem Schlüssel — direkt oder über Ecken?
 *
 * Die Gegenrichtung von `withDependencies`, und sie trägt die Kündigung: Eine
 * Basis abzubestellen, während Abhängige laufen, wird **abgelehnt** statt sie
 * stillschweigend mitzunehmen. Ein Datenverlust, den niemand beauftragt hat,
 * ist der schlechtere Ausgang als eine Fehlermeldung.
 */
export function dependentsOf(key: string): ModuleKey[] {
  const ergebnis = new Set<ModuleKey>()

  const sammle = (ziel: string) => {
    for (const m of MODULE_REGISTRY) {
      if (!m.dependsOn.includes(ziel as ModuleKey) || ergebnis.has(m.key)) continue
      ergebnis.add(m.key)
      sammle(m.key)
    }
  }

  sammle(key)
  return [...ergebnis]
}

/**
 * Darf dieser Eintrag gebucht werden?
 *
 * Zwei Gründe sprechen dagegen. **Reifegrad:** Ein unfertiges
 * (`IN_DEVELOPMENT`) oder nur geplantes Modul ist auch für den Betreiber nicht
 * buchbar — der Modulschalter ist gleichzeitig Feature-Flag, und ein Verkauf
 * würde beides vermischen. **Zerlegung (AP-29-N):** Eine Gruppe ist nie selbst
 * buchbar; gebucht werden ihre Bausteine, „das ganze Modul" ist ein Paket im
 * Katalog.
 *
 * Diese Funktion entscheidet über das **Verkaufen**, nicht über das *Gelten*
 * einer bestehenden Buchung — das prüft der `ModuleAccessService` bewusst nicht
 * mit. Sonst verlöre ein Mandant seinen Zugang in dem Augenblick, in dem sein
 * Modul zerlegt wird, statt in der Migration überführt zu werden.
 */
export function isBookable(key: string): boolean {
  const def = getModule(key)
  if (!def) return false
  // AP-30-A: `CORE` ist kein Produkt. Er gilt immer und wird nie gekauft —
  // stünde er im Katalog, könnte ihn jemand abbestellen und damit die
  // Benutzerverwaltung seines eigenen Mandanten abschalten.
  if (def.alwaysActive) return false
  if (!BOOKABLE_STATUSES.includes(def.status)) return false
  return !isModuleGroup(def.key)
}

/**
 * Was ein Mandant in `/settings/modules` zu sehen bekommt: alles Verkaufbare,
 * alles Geplante als Ausblick — und zusätzlich jedes Modul, das er tatsächlich
 * aktiv hat. Unfertige Module bleiben verborgen, solange sie niemand hat.
 */
/**
 * Was ein **neu registrierter** Mandant gebucht bekommt — samt Voraussetzungen.
 *
 * Die Liste entsteht aus zwei Bedingungen, und beide müssen zutreffen: Der
 * Eintrag ist verkaufbar (`isBookable`) **und** ausdrücklich als Starteintrag
 * markiert. Ein neuer Modulschlüssel ist damit von sich aus **aus** — er muss
 * verkauft werden, statt still bei jedem Neukunden aufzutauchen.
 */
export function startModules(): string[] {
  const markiert = MODULE_REGISTRY.filter(
    (m) => m.includedForNewTenants === true && isBookable(m.key),
  ).map((m) => m.key)

  // Voraussetzungen mitnehmen: Ein Starteintrag ohne seine Basis wäre eine
  // Buchung ins Leere — dieselbe Regel wie beim Buchen in der Konsole.
  return withDependencies(markiert).filter((k) => isBookable(k))
}

export function visibleModules(activeKeys: readonly string[]): ModuleDefinition[] {
  const active = new Set(activeKeys)
  return MODULE_REGISTRY.filter(
    // AP-30-A: `CORE` gehört nicht auf die Modulseite. Er ist kein Angebot und
    // keine Buchung, sondern die Voraussetzung — ein Eintrag „immer aktiv, nicht
    // abwählbar" wäre dort nur eine Zeile, die niemand bedienen kann.
    (m) => !m.alwaysActive && (m.status !== 'IN_DEVELOPMENT' || active.has(m.key)),
  )
}
